// ---------------------------------------------------------------------------
// Settings — persistent client preferences (control scheme, keybindings,
// camera behavior…) with a settings panel UI. Designed to grow: add a field
// to SettingsData + DEFAULTS and (optionally) a control in buildPanel.
// ---------------------------------------------------------------------------

export type ControlScheme = "classic" | "wasd";
export type ShadowQuality = "off" | "low" | "medium" | "high" | "ultra";

/** A saved "bag" entry: serialized objects + the defs they need. */
export interface BagEntry {
  name: string;
  at: number;
  /** Def to draw as this entry's thumbnail (the object that was bagged). */
  thumbDef?: string;
  data: { objects: unknown[]; defs: unknown[] };
}

/** Rebindable actions. Digits (draw N) and mouse gestures are fixed. */
export const ACTIONS = [
  "panUp", "panLeft", "panDown", "panRight",
  "flip", "rotateLeft", "rotateRight", "shuffle",
  "lock", "delete", "zoomCard", "toggleMode", "help",
  "sortHand", "findCard", "chat",
  // MTG (defaults follow Proxy Table where they don't clash with WASD)
  "drawCard", "toGrave", "toExile", "toHand", "mulligan", "newTurn", "plusOne", "lifeUp", "lifeDown",
] as const;
export type ActionId = (typeof ACTIONS)[number];

const ACTION_LABELS: Record<ActionId, string> = {
  panUp: "Pan up",
  panLeft: "Pan left",
  panDown: "Pan down",
  panRight: "Pan right",
  flip: "Flip object",
  rotateLeft: "Rotate left",
  rotateRight: "Rotate right",
  shuffle: "Shuffle",
  lock: "Lock / unlock",
  delete: "Delete",
  zoomCard: "Zoom card (hold)",
  toggleMode: "Toggle 2D / 3D",
  help: "Help",
  sortHand: "Sort my hand",
  findCard: "Find card in hand",
  chat: "Table chat",
  drawCard: "MTG: draw a card",
  toGrave: "MTG: send to graveyard",
  toExile: "MTG: send to exile",
  toHand: "MTG: return to hand",
  mulligan: "MTG: mulligan (hand → deck, draw 7)",
  newTurn: "MTG: new turn (untap+draw)",
  plusOne: "MTG: add +1/+1 counter",
  lifeUp: "MTG: life +1",
  lifeDown: "MTG: life −1",
};

export interface SettingsData {
  version: number;
  scheme: ControlScheme;
  panSpeed: number; // multiplier
  /** Rotation step for Q/E and wheel-while-dragging, in degrees. */
  rotateStep: number;
  /** Card cell width (px) in the deck inspector grid. */
  inspectorCardSize: number;
  shadowQuality: ShadowQuality;
  /** Renderer pixel-ratio cap (1 = standard, 2 = sharp, 3 = max). */
  renderScale: number;
  /** Procedural sound effects (card flips, shuffles, dice…). */
  soundOn: boolean;
  soundVolume: number; // 0..1
  binds: Record<ActionId, string>; // KeyboardEvent.key, lowercased
  /** Saved object bags (synced to the account settings blob when signed in). */
  bags: BagEntry[];
}

// NOTE: shuffle is R (not S) so it never fights WASD panning.
const DEFAULTS: SettingsData = {
  version: 2,
  scheme: "wasd",
  panSpeed: 1,
  rotateStep: 90,
  inspectorCardSize: 92,
  shadowQuality: "medium",
  renderScale: 2,
  soundOn: true,
  soundVolume: 0.5,
  binds: {
    panUp: "w", panLeft: "a", panDown: "s", panRight: "d",
    flip: "f", rotateLeft: "q", rotateRight: "e", shuffle: "r",
    lock: "l", delete: "delete", zoomCard: "alt", toggleMode: "v", help: "h",
    sortHand: "t", findCard: "/", chat: "enter",
    // Proxy Table parity: g=grave, n=new turn; draw is c (d pans right here).
    drawCard: "c", toGrave: "g", toExile: "x", toHand: "b", mulligan: "m", newTurn: "n",
    plusOne: "", lifeUp: "", lifeDown: "",
  },
  bags: [],
};

const LS_KEY = "tt.settings";

export class Settings {
  data: SettingsData;
  private listeners: Array<() => void> = [];
  private panel: HTMLElement | null = null;
  /** While rebinding, the next keydown is captured by the panel. */
  capturing: ActionId | null = null;
  private activeTab = "controls";

  constructor() {
    this.data = structuredClone(DEFAULTS);
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        this.data = {
          ...structuredClone(DEFAULTS),
          ...saved,
          binds: { ...DEFAULTS.binds, ...(saved.binds ?? {}) },
        };
        // v2 migration: WASD+select became the default scheme, rotation 90°.
        if ((saved.version ?? 1) < 2) {
          this.data.version = 2;
          this.data.scheme = DEFAULTS.scheme;
          this.data.rotateStep = DEFAULTS.rotateStep;
          if (!this.data.binds.zoomCard) this.data.binds.zoomCard = "alt";
        }
      }
    } catch { /* corrupted settings → defaults */ }
  }

  save(): void {
    localStorage.setItem(LS_KEY, JSON.stringify(this.data));
    for (const fn of this.listeners) fn();
    // Decoupled modules (account sync) listen for this.
    window.dispatchEvent(new CustomEvent("tt-settings-changed", { detail: this.data }));
  }

  /** Adopt remotely-synced settings (account login) without re-broadcasting. */
  applyRemote(data: Partial<SettingsData>): void {
    this.data = {
      ...this.data,
      ...data,
      binds: { ...this.data.binds, ...(data.binds ?? {}) },
    };
    localStorage.setItem(LS_KEY, JSON.stringify(this.data));
    for (const fn of this.listeners) fn();
    if (this.isOpen) this.refreshPanel();
  }

  onChange(fn: () => void): void {
    this.listeners.push(fn);
  }

  /** Action bound to a key, or null. */
  actionFor(key: string): ActionId | null {
    key = key.toLowerCase();
    for (const a of ACTIONS) if (this.data.binds[a] === key) return a;
    return null;
  }

  keyFor(action: ActionId): string {
    return this.data.binds[action];
  }

  /** Pretty key name for UI. */
  static keyName(key: string): string {
    if (key === " ") return "Space";
    if (key.length === 1) return key.toUpperCase();
    return key[0]!.toUpperCase() + key.slice(1);
  }

  // ---------------------------------------------------------------- panel

  toggle(root: HTMLElement): void {
    if (this.panel) {
      this.close();
      return;
    }
    this.panel = this.buildPanel();
    root.append(this.panel);
  }

  close(): void {
    this.panel?.remove();
    this.panel = null;
    this.capturing = null;
  }

  get isOpen(): boolean {
    return this.panel !== null;
  }

  /** Panel-level key handling while rebinding. Returns true if consumed. */
  handleKeyCapture(e: KeyboardEvent): boolean {
    if (!this.capturing) return false;
    e.preventDefault();
    e.stopPropagation();
    if (e.key !== "Escape") {
      const key = e.key.toLowerCase();
      // Steal the key from any action that already uses it.
      for (const a of ACTIONS) {
        if (this.data.binds[a] === key) this.data.binds[a] = "";
      }
      this.data.binds[this.capturing] = key;
      this.save();
    }
    this.capturing = null;
    this.refreshPanel();
    return true;
  }

  private refreshPanel(): void {
    if (!this.panel) return;
    const parent = this.panel.parentElement!;
    this.panel.remove();
    this.panel = this.buildPanel();
    parent.append(this.panel);
  }

  private buildPanel(): HTMLElement {
    const p = document.createElement("div");
    p.className = "settings";
    p.innerHTML = `<h2>Settings</h2>`;

    // Tab bar.
    const tabs: Array<[string, string]> = [
      ["controls", "Controls"],
      ["graphics", "Graphics"],
      ["keys", "Keybindings"],
    ];
    const bar = document.createElement("div");
    bar.className = "settings-tabs";
    for (const [id, label] of tabs) {
      const b = document.createElement("button");
      b.className = "settings-tab" + (this.activeTab === id ? " active" : "");
      b.textContent = label;
      b.onclick = () => {
        this.activeTab = id;
        this.refreshPanel();
      };
      bar.append(b);
    }
    p.append(bar);

    if (this.activeTab === "controls") this.buildControlsTab(p);
    else if (this.activeTab === "graphics") this.buildGraphicsTab(p);
    else this.buildKeysTab(p);

    const footer = document.createElement("div");
    footer.className = "settings-footer";
    const reset = document.createElement("button");
    reset.className = "chip";
    reset.textContent = "Reset to defaults";
    reset.onclick = () => {
      this.data = structuredClone(DEFAULTS);
      this.save();
      this.refreshPanel();
    };
    const close = document.createElement("button");
    close.className = "chip";
    close.textContent = "Close";
    close.onclick = () => this.close();
    footer.append(reset, close);
    p.append(footer);
    return p;
  }

  private buildControlsTab(p: HTMLElement): void {
    const controls = section(p, "Controls");
    const schemeRow = row(controls, "Control scheme");
    const schemeSel = document.createElement("select");
    schemeSel.innerHTML = `
      <option value="wasd">WASD — drag empty space box-selects</option>
      <option value="classic">Classic — drag empty space pans</option>`;
    schemeSel.value = this.data.scheme;
    schemeSel.onchange = () => {
      this.data.scheme = schemeSel.value as ControlScheme;
      this.save();
    };
    schemeRow.append(schemeSel);
    const hint = document.createElement("p");
    hint.className = "settings-hint";
    hint.textContent =
      this.data.scheme === "wasd"
        ? "Camera moves with the pan keys only; left-drag on empty table selects multiple objects."
        : "Left-drag on empty table pans the camera. Pan keys also work.";
    controls.append(hint);

    const rotRow = row(controls, "Rotation step");
    const rotSel = document.createElement("select");
    rotSel.innerHTML = [15, 30, 45, 60, 90]
      .map((d) => `<option value="${d}">${d}°</option>`)
      .join("");
    rotSel.value = String(this.data.rotateStep);
    rotSel.onchange = () => {
      this.data.rotateStep = Number(rotSel.value);
      this.save();
    };
    rotRow.append(rotSel);

    const speedRow = row(controls, "Pan speed");
    const speed = document.createElement("input");
    speed.type = "range";
    speed.min = "0.4";
    speed.max = "2.5";
    speed.step = "0.1";
    speed.value = String(this.data.panSpeed);
    speed.oninput = () => {
      this.data.panSpeed = Number(speed.value);
      this.save();
    };
    speedRow.append(speed);

    const snd = section(p, "Sound");
    const onRow = row(snd, "Sound effects");
    const onBox = document.createElement("input");
    onBox.type = "checkbox";
    onBox.checked = this.data.soundOn;
    onBox.onchange = () => {
      this.data.soundOn = onBox.checked;
      this.save();
      if (onBox.checked) window.dispatchEvent(new CustomEvent("tt-test-sound"));
    };
    onRow.append(onBox);
    const volRow = row(snd, "Volume");
    const vol = document.createElement("input");
    vol.type = "range";
    vol.min = "0";
    vol.max = "1";
    vol.step = "0.05";
    vol.value = String(this.data.soundVolume);
    vol.oninput = () => {
      this.data.soundVolume = Number(vol.value);
      this.save();
    };
    vol.onchange = () => window.dispatchEvent(new CustomEvent("tt-test-sound")); // audible feedback
    volRow.append(vol);
  }

  private buildGraphicsTab(p: HTMLElement): void {
    const gfx = section(p, "Graphics");

    const shadowRow = row(gfx, "Shadow quality");
    const shadowSel = document.createElement("select");
    shadowSel.innerHTML = `
      <option value="off">Off</option>
      <option value="low">Low</option>
      <option value="medium">Medium</option>
      <option value="high">High</option>
      <option value="ultra">Ultra</option>`;
    shadowSel.value = this.data.shadowQuality;
    shadowSel.onchange = () => {
      this.data.shadowQuality = shadowSel.value as ShadowQuality;
      this.save();
    };
    shadowRow.append(shadowSel);

    const scaleRow = row(gfx, "Render sharpness");
    const scaleSel = document.createElement("select");
    scaleSel.innerHTML = `
      <option value="1">Standard</option>
      <option value="2">Sharp</option>
      <option value="3">Supersampled</option>
      <option value="4">Extreme</option>`;
    scaleSel.value = String(this.data.renderScale);
    scaleSel.onchange = () => {
      this.data.renderScale = Number(scaleSel.value);
      this.save();
    };
    scaleRow.append(scaleSel);

    const sizeRow = row(gfx, "Deck-view card size");
    const size = document.createElement("input");
    size.type = "range";
    size.min = "60";
    size.max = "260";
    size.step = "5";
    size.value = String(this.data.inspectorCardSize);
    size.oninput = () => {
      this.data.inspectorCardSize = Number(size.value);
      this.save();
    };
    sizeRow.append(size);
    const hint = document.createElement("p");
    hint.className = "settings-hint";
    hint.textContent = "Deck-view size applies live; shadow/sharpness changes rebuild the renderer.";
    gfx.append(hint);
  }

  private buildKeysTab(p: HTMLElement): void {
    const keys = section(p, "Keybindings");
    for (const a of ACTIONS) {
      const r = row(keys, ACTION_LABELS[a]);
      const btn = document.createElement("button");
      btn.className = "chip bind-btn";
      btn.textContent =
        this.capturing === a
          ? "press a key…"
          : this.data.binds[a]
            ? Settings.keyName(this.data.binds[a])
            : "—";
      if (this.capturing === a) btn.classList.add("capturing");
      btn.onclick = () => {
        this.capturing = this.capturing === a ? null : a;
        this.refreshPanel();
      };
      r.append(btn);
    }
  }
}

function section(parent: HTMLElement, title: string): HTMLElement {
  const s = document.createElement("div");
  s.className = "settings-section";
  const h = document.createElement("h3");
  h.textContent = title;
  s.append(h);
  parent.append(s);
  return s;
}

function row(parent: HTMLElement, label: string): HTMLElement {
  const r = document.createElement("div");
  r.className = "settings-row";
  const l = document.createElement("span");
  l.textContent = label;
  r.append(l);
  parent.append(r);
  return r;
}
